import { Link } from 'react-router-dom';
import { Users, Play, ChevronDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import type { Round, League } from '@/lib/mock-data';
import { useStore } from '@/lib/store';
import { useMemo, useEffect, useState, useRef } from 'react';
import * as api from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { getSmartlinkUrl } from '@/lib/smartlinks';

interface RoundCardProps {
  round: Round;
  leagueId: string;
  isCurrentRound?: boolean;
  league?: League;
  seasonStatus?: 'active' | 'completed' | 'upcoming'; // Season status to determine if "Reopen Voting" should be shown
  mode?: 'live' | 'archive';
}

const RoundCard = ({ round, leagueId, isCurrentRound = false, league, seasonStatus, mode = 'live' }: RoundCardProps) => {
  const { state, getSubmissionsByRound, getSubmissionByUserAndRound, updateRound, hasSubmittedVotes, addVotesToState, getVotesByRound, addSubmissionsToState } = useStore();
  const { currentUser } = state;
  const [votesLoaded, setVotesLoaded] = useState(false);
  const [submissionsLoaded, setSubmissionsLoaded] = useState(false);
  const [isChangingStatus, setIsChangingStatus] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false); // Track if round is expanded to show votes/comments (archive mode)
  const [expandedSubmissionId, setExpandedSubmissionId] = useState<string | null>(null); // Track which submission's votes are expanded
  const { toast } = useToast();
  
  // Get the latest round from store to ensure we have the most up-to-date status
  const currentRound = useMemo(() => {
    const storeRound = state.rounds.find(r => r.id === round.id);
    if (storeRound && storeRound.status !== round.status) {
      console.log('[RoundCard] Status mismatch - store:', storeRound.status, 'prop:', round.status);
    }
    return storeRound || round;
  }, [state.rounds, round]);
  
  // Refresh round status from API periodically to ensure all users see latest status
  // But only refresh when needed and with proper error handling
  useEffect(() => {
    if (mode === 'archive') return;
    if (!round.id) return;
    
    const isSupabaseConfigured = () => {
      const url = import.meta.env.VITE_SUPABASE_URL;
      const key = import.meta.env.VITE_SUPABASE_ANON_KEY;
      return !!(url && key && url !== 'https://placeholder.supabase.co' && key !== 'placeholder-key');
    };
    
    if (!isSupabaseConfigured()) return;
    
    let errorCount = 0;
    const MAX_ERRORS = 3;
    let isRefreshing = false;
    
    // Refresh round status from API
    const refreshRound = async () => {
      // Prevent concurrent refreshes
      if (isRefreshing) return;
      
      // Stop refreshing if we've had too many errors
      if (errorCount >= MAX_ERRORS) {
        console.log('[RoundCard] Stopping refresh due to too many errors');
        return;
      }
      
      isRefreshing = true;
      try {
        const latestRound = await api.roundAPI.getRound(round.id);
        if (latestRound) {
          // Always update to ensure we have the latest data, especially for status changes
          // This ensures all users see status changes immediately
          if (latestRound.status !== currentRound.status) {
            console.log('[RoundCard] Round status changed:', latestRound.status, 'was:', currentRound.status);
          }
          // Update the round to sync status across all users
          updateRound(latestRound.id, latestRound);
          // Reset error count on success
          errorCount = 0;
        }
      } catch (error) {
        errorCount++;
        console.error('[RoundCard] Failed to refresh round:', error);
        // Don't log every error to avoid spam
        if (errorCount === 1) {
          console.warn('[RoundCard] Will stop refreshing after', MAX_ERRORS, 'errors');
        }
      } finally {
        isRefreshing = false;
      }
    };
    
    // Refresh immediately on mount (with a small delay to avoid race conditions)
    const initialTimeout = setTimeout(refreshRound, 1000);
    
    // Refresh every 30 seconds (reduced from 5 seconds to avoid rate limiting)
    // Only if page is visible
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible' && errorCount < MAX_ERRORS) {
        refreshRound();
      }
    }, 30000);
    
    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, [round.id, updateRound]); // Don't include currentRound.status to avoid re-creating interval on every status change
  
  // Check if user is admin or league creator
  const isAdmin = currentUser?.isAdmin || (league && league.created_by === currentUser?.id);
  
  // Fetch votes from Supabase for voting rounds to check if user has submitted
  useEffect(() => {
    if (mode === 'archive') return;
    if (currentRound.status !== 'voting' || !currentUser || votesLoaded) return;
    
    const isSupabaseConfigured = () => {
      const url = import.meta.env.VITE_SUPABASE_URL;
      const key = import.meta.env.VITE_SUPABASE_ANON_KEY;
      return !!(url && key && url !== 'https://placeholder.supabase.co' && key !== 'placeholder-key');
    };
    
    if (!isSupabaseConfigured()) {
      setVotesLoaded(true);
      return;
    }
    
    // Fetch votes for this round to check submission status
    api.voteAPI.getVotesByRound(currentRound.id)
      .then((votes) => {
        if (votes.length > 0) {
          addVotesToState(votes);
        }
        setVotesLoaded(true);
      })
      .catch((error) => {
        console.error('Failed to fetch votes:', error);
        setVotesLoaded(true);
      });
  }, [currentRound.status, currentRound.id, currentUser, votesLoaded, addVotesToState]);

  // Fetch votes when expanded for completed rounds
  useEffect(() => {
    if (!isExpanded) {
      // Reset votesLoaded when collapsed so we fetch again when expanded
      if (!isExpanded) {
        setVotesLoaded(false);
      }
      return;
    }
    
    if (votesLoaded) return; // Already loaded

    // In archive mode we always show votes/comments when expanded (no status display, but we still fetch data)
    if (mode !== 'archive' && currentRound.status !== 'completed') {
      return;
    }
    
    const isSupabaseConfigured = () => {
      const url = import.meta.env.VITE_SUPABASE_URL;
      const key = import.meta.env.VITE_SUPABASE_ANON_KEY;
      return !!(url && key && url !== 'https://placeholder.supabase.co' && key !== 'placeholder-key');
    };
    
    if (!isSupabaseConfigured()) return;

    const fetchVotes = async () => {
      try {
        const votes = await api.voteAPI.getVotesByRound(currentRound.id);
        if (votes.length > 0) {
          addVotesToState(votes);
        }
        setVotesLoaded(true);
      } catch (error) {
        console.error('Failed to fetch votes:', error);
        setVotesLoaded(true);
      }
    };

    fetchVotes();
  }, [isExpanded, currentRound.status, currentRound.id, votesLoaded, addVotesToState, mode]);

  // Get votes for each submission
  const votesBySubmission = useMemo(() => {
    if (mode === 'live' && currentRound.status !== 'completed') return new Map<string, typeof state.votes>();

    const allVotes = getVotesByRound(currentRound.id);
    const effectiveVotes =
      mode === 'archive'
        ? allVotes // historical imports may not set is_submitted; we still want comments/scores
        : allVotes.filter((v) => v.is_submitted);

    const map = new Map();
    effectiveVotes.forEach(vote => {
      const existing = map.get(vote.submission_id) || [];
      map.set(vote.submission_id, [...existing, vote]);
    });
    return map;
  }, [currentRound.id, currentRound.status, getVotesByRound, state.votes, state, mode]);

  // Fetch submissions for this round if not already loaded
  useEffect(() => {
    if (mode === 'archive' && !isExpanded) return;
    const existingSubmissions = getSubmissionsByRound(currentRound.id);
    if (existingSubmissions.length > 0) {
      setSubmissionsLoaded(true);
      return; // Already loaded
    }

    const isSupabaseConfigured = () => {
      const url = import.meta.env.VITE_SUPABASE_URL;
      const key = import.meta.env.VITE_SUPABASE_ANON_KEY;
      return !!(url && key && url !== 'https://placeholder.supabase.co' && key !== 'placeholder-key');
    };

    if (!isSupabaseConfigured()) {
      setSubmissionsLoaded(true);
      return;
    }

    api.submissionAPI.getSubmissionsByRound(currentRound.id)
      .then((submissions) => {
        if (submissions && submissions.length > 0) {
          addSubmissionsToState(submissions);
        }
        setSubmissionsLoaded(true);
      })
      .catch((error) => {
        console.error('[RoundCard] Failed to fetch submissions:', error);
        setSubmissionsLoaded(true);
      });
  }, [currentRound.id, getSubmissionsByRound, addSubmissionsToState, mode, isExpanded]);

  // Get submissions for this round - ensure we're filtering by the correct round_id
  const roundSubmissions = useMemo(() => {
    const submissions = getSubmissionsByRound(currentRound.id);
    // Double-check filtering by round_id to ensure we only get submissions for this specific round
    const filtered = submissions.filter(s => s.round_id === currentRound.id);
    console.log('[RoundCard] Submissions for round:', {
      roundId: currentRound.id,
      roundTheme: currentRound.theme,
      roundNumber: currentRound.round_number,
      totalSubmissions: submissions.length,
      filteredSubmissions: filtered.length,
      submissionRoundIds: submissions.map(s => ({ id: s.id, round_id: s.round_id, song: s.song_title }))
    });
    return filtered.sort((a, b) => (b.total_points ?? 0) - (a.total_points ?? 0));
  }, [currentRound.id, currentRound.theme, currentRound.round_number, getSubmissionsByRound, state.submissions]);

  // Track if we're currently changing status to prevent automatic transitions
  const isManuallyChangingStatusRef = useRef(false);
  
  // Check if round was manually reopened (status is 'submitting' but deadline has passed)
  // If so, never auto-transition - admin must manually close it
  const wasManuallyReopened = useMemo(() => {
    if (currentRound.status !== 'submitting') return false;
    const deadline = new Date(currentRound.submission_deadline);
    const now = new Date();
    return now > deadline; // If deadline passed but status is still 'submitting', it was manually reopened
  }, [currentRound]);
  
  // Check and transition round status if submissions are locked
  // BUT: NEVER auto-transition if round was manually reopened by admin
  // Manually reopened rounds stay open until admin manually closes them
  useEffect(() => {
    if (mode === 'archive') return;
    if (currentRound.status !== 'submitting' || !league) {
      isManuallyChangingStatusRef.current = false; // Reset flag when status changes away from submitting
      return;
    }
    
    // Don't auto-transition if we just manually changed the status
    if (isManuallyChangingStatusRef.current) {
      // Reset the flag after a short delay to allow manual changes to complete
      setTimeout(() => {
        isManuallyChangingStatusRef.current = false;
      }, 2000);
      return;
    }
    
    // If round was manually reopened (deadline passed but still 'submitting'),
    // NEVER auto-transition - admin must manually close it
    if (wasManuallyReopened) {
      console.log('[RoundCard] Round was manually reopened, skipping auto-transition');
      return;
    }
    
    const deadline = new Date(currentRound.submission_deadline);
    const now = new Date();
    const isDeadlinePassed = now > deadline;
    
    const allSubmissions = getSubmissionsByRound(currentRound.id);
    const allPlayersSubmitted = league.max_players > 0 && allSubmissions.length >= league.max_players;
    
    // Only auto-transition if deadline hasn't passed yet AND all players have submitted
    // (Normal flow - not a manually reopened round)
    if (!isDeadlinePassed && allPlayersSubmitted) {
      // All players submitted before deadline, safe to transition
      const isSupabaseConfigured = () => {
        const url = import.meta.env.VITE_SUPABASE_URL;
        const key = import.meta.env.VITE_SUPABASE_ANON_KEY;
        return !!(url && key && url !== 'https://placeholder.supabase.co' && key !== 'placeholder-key');
      };
      
      if (isSupabaseConfigured()) {
        // Check and transition in Supabase
        api.roundAPI.checkAndTransitionRoundStatus(currentRound.id, league.max_players)
          .then(updatedRound => {
            if (updatedRound && updatedRound.status === 'voting') {
              // Update local state
              updateRound(currentRound.id, { status: 'voting' });
            }
          })
          .catch(error => {
            console.error('Failed to transition round status:', error);
          });
      } else {
        // For localStorage mode, just update local state
        updateRound(currentRound.id, { status: 'voting' });
      }
    }
  }, [currentRound, league, getSubmissionsByRound, updateRound, wasManuallyReopened]);
  
  // Get submission info
  const submissionInfo = useMemo(() => {
    if (mode === 'archive') return null;
    if (!currentUser || currentRound.status !== 'submitting') return null;
    
    const allSubmissions = getSubmissionsByRound(currentRound.id);
    const userSubmission = getSubmissionByUserAndRound(currentUser.id, currentRound.id);
    
    // Check if user can still edit
    const deadline = new Date(currentRound.submission_deadline);
    const now = new Date();
    const isDeadlinePassed = now > deadline;
    const maxPlayers = league?.max_players || 0;
    const allPlayersSubmitted = maxPlayers > 0 && allSubmissions.length >= maxPlayers;
    const canEdit = !isDeadlinePassed && !allPlayersSubmitted;
    
    return {
      userSubmission,
      allSubmissions,
      submissionCount: allSubmissions.length,
      totalMembers: maxPlayers,
      canEdit,
      isDeadlinePassed,
      allMembersSubmitted: allPlayersSubmitted,
    };
  }, [currentRound, league, currentUser, getSubmissionsByRound, getSubmissionByUserAndRound]);

  // No action buttons - submissions and voting happen on MusicLeague.com
  const getActionButton = () => {
    return null;
  };

  const getTimeRemaining = () => {
    const deadline = currentRound.status === 'submitting' 
      ? new Date(currentRound.submission_deadline) 
      : new Date(currentRound.voting_deadline);
    
    const now = new Date();
    const diff = deadline.getTime() - now.getTime();
    
    if (diff <= 0) return 'Deadline passed';
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) return `${days}d ${hours}h left`;
    return `${hours}h left`;
  };

  const archiveVoterComments = (submissionId: string) => {
    const votes = (votesBySubmission.get(submissionId) || []) as any[];
    return votes
      .map((v) => ({
        username: v.username || 'Unknown',
        points: typeof v.points === 'number' ? v.points : 0,
        comment: (v.comment || '').trim(),
      }))
      .filter((v) => v.comment.length > 0);
  };

  if (mode === 'archive') {
    return (
      <Card className={`card-hover ${isExpanded ? 'ring-1 ring-primary/30' : ''}`}>
        <button
          type="button"
          className="w-full text-left"
          onClick={() => setIsExpanded((prev) => !prev)}
          aria-expanded={isExpanded}
        >
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <CardTitle className="text-xl md:text-2xl font-bold">
                  {currentRound.round_number}. {currentRound.theme}
                </CardTitle>
                {currentRound.description && (
                  <p className="mt-2 text-sm text-muted-foreground">{currentRound.description}</p>
                )}
                <div className="mt-3 text-xs text-muted-foreground">
                  {isExpanded ? 'Click to collapse' : 'Click to expand'}
                </div>
              </div>
              <div className="pt-1 text-muted-foreground">
                <ChevronDown className={`h-5 w-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
              </div>
            </div>
          </CardHeader>
        </button>

        {isExpanded && (
          <CardContent className="pt-0">
            {/* Smartlinks Widget - Show at top when expanded */}
            {getSmartlinkUrl(currentRound) && (
              <div className="mt-2 mb-4 pt-4 border-t border-muted">
                <h4 className="text-sm font-semibold text-foreground mb-2">Stream All Songs</h4>
                <div className="w-full overflow-hidden rounded-lg">
                  <iframe
                    frameBorder="0"
                    width="100%"
                    height="300"
                    style={{ maxWidth: '100%' }}
                    src={getSmartlinkUrl(currentRound) || ''}
                    title={`Smartlinks for ${currentRound.theme}`}
                  />
                </div>
              </div>
            )}

            {/* Submissions - ranked by score */}
            {!submissionsLoaded ? (
              <div className="pt-4 border-t border-muted">
                <div className="text-sm text-muted-foreground">Loading submissions…</div>
              </div>
            ) : roundSubmissions.length === 0 ? (
              <div className="pt-4 border-t border-muted">
                <div className="text-sm text-muted-foreground">No submissions found for this round.</div>
              </div>
            ) : (
              <div className="pt-4 border-t border-muted">
                <h4 className="text-sm font-semibold text-foreground mb-3">Submissions</h4>
                <div className="space-y-4">
                  {roundSubmissions.map((submission, index) => {
                    const user = state.users.find(u => u.id === submission.user_id);
                    const voterComments = archiveVoterComments(submission.id);

                    return (
                      <div key={submission.id} className="pl-4 border-l-2 border-muted">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-medium text-muted-foreground">#{index + 1}</span>
                              <h5 className="text-sm font-semibold text-foreground">{submission.song_title}</h5>
                              <span className="text-xs text-muted-foreground">by {submission.artist_name}</span>
                            </div>
                            {(user || submission.username) && (
                              <div className="mt-1 text-xs text-muted-foreground">
                                Submitted by: {submission.username || user?.username || 'Unknown'}
                              </div>
                            )}

                            {/* Submitter comment */}
                            {submission.submitter_comment && (
                              <p className="text-xs text-muted-foreground italic mt-2 pl-3 border-l border-muted">
                                "{submission.submitter_comment}"
                              </p>
                            )}

                            {/* Voter comments */}
                            {voterComments.length > 0 && (
                              <div className="mt-3 space-y-2">
                                <div className="text-xs font-semibold text-foreground/80">Comments</div>
                                {voterComments.map((c, i) => (
                                  <div key={`${submission.id}-vc-${i}`} className="text-xs text-muted-foreground pl-3 border-l border-muted">
                                    <span className="font-medium text-foreground/80">{c.username}:</span> {c.comment}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="text-right shrink-0">
                            <div className="text-sm font-bold text-primary">{submission.total_points ?? 0}</div>
                            <div className="text-xs text-muted-foreground">points</div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        )}
      </Card>
    );
  }

  return (
    <Card 
      className={`${isCurrentRound ? 'ring-2 ring-primary card-shadow' : 'card-hover'} ${isCurrentRound ? 'animate-pulse-glow' : ''} ${currentRound.status === 'completed' ? 'cursor-pointer' : ''}`}
      onClick={() => {
        // No expand/collapse - submissions always visible
      }}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-2">
              <CardTitle className="text-lg">{currentRound.theme}</CardTitle>
              {isCurrentRound && (
                <Badge variant="default" className="bg-music-gradient text-white">
                  Current
                </Badge>
              )}
            </div>
            <h3 className="text-xl font-semibold text-primary mb-2">
              {currentRound.theme}
            </h3>
            {currentRound.description && (
              <p className="text-sm text-muted-foreground">
                {currentRound.description}
              </p>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-0">



        <div onClick={(e) => e.stopPropagation()}>
          {getActionButton()}
        </div>

        {/* Smartlinks Widget - Show for any round with Smartlink URL */}
        {getSmartlinkUrl(currentRound) && (
          <div className="mt-4 pt-4 border-t border-muted">
            <h4 className="text-sm font-semibold text-foreground mb-2">Stream All Songs</h4>
            <div className="w-full overflow-hidden rounded-lg">
              <iframe
                frameBorder="0"
                width="100%"
                height="300"
                style={{ maxWidth: '100%' }}
                src={getSmartlinkUrl(currentRound) || ''}
                title={`Smartlinks for ${currentRound.theme}`}
              />
            </div>
          </div>
        )}

        {/* Submissions - Show for any round */}
        {roundSubmissions.length > 0 && (
          <div className="mt-4 pt-4 border-t border-muted">
            <h4 className="text-sm font-semibold text-foreground mb-3">Submissions</h4>
            <div className="space-y-3">
              {roundSubmissions.map((submission, index) => {
                const user = state.users.find(u => u.id === submission.user_id);
                
                return (
                  <div key={submission.id} className="pl-4 border-l-2 border-muted">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-muted-foreground">#{index + 1}</span>
                          <h5 className="text-sm font-semibold text-foreground">{submission.song_title}</h5>
                          <span className="text-xs text-muted-foreground">by {submission.artist_name}</span>
                        </div>
                        {(user || submission.username) && (
                          <div className="flex items-center gap-1 mt-1">
                            <span className="text-xs text-muted-foreground">
                              Submitted by: {submission.username || user?.username || 'Unknown'}
                            </span>
                          </div>
                        )}
                        {submission.submitter_comment && (
                          <p className="text-xs text-muted-foreground italic mt-1 pl-4">
                            "{submission.submitter_comment}"
                          </p>
                        )}
                      </div>
                      {submission.total_points > 0 && (
                        <div className="text-right">
                          <div className="text-sm font-bold text-primary">{submission.total_points}</div>
                          <div className="text-xs text-muted-foreground">points</div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RoundCard;